import React, { useEffect, useState } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import { formatCurrency } from '../../utils/formatCurrency';
import { quoteAPI } from '../../services/api';
import { sanitizeTcHtml } from '../../utils/sanitizeTcHtml';

/**
 * AcceptedConfigurationModal — IDI-116.
 *
 * "The signed acceptance is unreachable — signature image has no reader,
 * getAcceptedConfiguration has no caller, and no signed contract document
 * exists."
 *
 * This closes the first two defects: it IS the caller for
 * getAcceptedConfiguration, and it fetches + renders the signature image via
 * the new dedicated getSignatureDownloadUrl endpoint (cmd/quotes.
 * getSignatureDownloadURL — the general getDownloadURL's tenant-prefix check
 * can never match a "signatures/<tenantId>/..." key, so no signature image
 * was ever reachable through it regardless of whether a caller existed).
 *
 * A signed CONTRACT DOCUMENT (PDF) is tracked separately pending IDI-115's
 * "what does download mean on this app" decision — this modal is the
 * ON-SCREEN acceptance record; it does not claim to BE the document.
 *
 * Opened by clicking the existing LockedAtAcceptancePill (PT-43.2).
 */
export default function AcceptedConfigurationModal({ open, onClose, quoteId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [signatureError, setSignatureError] = useState(null);

  useEffect(() => {
    if (!open || !quoteId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setConfig(null);
    setSignatureUrl(null);
    setSignatureError(null);

    (async () => {
      try {
        const res = await quoteAPI.getAcceptedConfiguration(quoteId);
        const data = res?.data || res;
        if (cancelled) return;
        setConfig(data || null);
        if (!data) {
          setError('This quote has no recorded acceptance.');
        }
      } catch (err) {
        if (!cancelled) {
          setError('Could not load the acceptance record. Try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }

      // Signature image is optional — a contractor-recorded decision may
      // have no drawn signature at all. Failure here must not block the
      // rest of the acceptance record from rendering.
      try {
        const sigRes = await quoteAPI.getSignatureDownloadUrl(quoteId);
        const sigData = sigRes?.data || sigRes;
        if (!cancelled && sigData?.download_url) {
          setSignatureUrl(sigData.download_url);
        }
      } catch (err) {
        if (!cancelled) {
          // Not a page-level error — a quote that was accepted without a
          // drawn signature is a normal, valid state (see
          // cmd/signature.storeSignatureImage's own doc comment).
          setSignatureError('No signature image on file for this acceptance.');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [open, quoteId]);

  // IDI-812 D3: the frozen terms, cleaned and formatted (IDI-624's cleaner);
  // plain text keeps its line breaks; nothing visible, no section.
  const rawTerms = (config && (config.terms_conditions || config.termsConditions)) || '';
  const cleanTerms = sanitizeTcHtml(typeof rawTerms === 'string' ? rawTerms : '');
  const termsArePlain = !/<[a-z][\s\S]*>/i.test(rawTerms);

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogHeader onClose={onClose}>Signed Acceptance</DialogHeader>
      <DialogBody>
        {loading && <p className="text-sm text-content-secondary">Loading acceptance record…</p>}
        {error && <Alert variant="error">{error}</Alert>}

        {!loading && config && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-content-secondary uppercase tracking-wide">Accepted By</p>
                <p className="text-sm font-medium text-content-primary">
                  {config.accepted_by || config.acceptedBy || '—'}
                </p>
                {(config.accepted_by_email || config.acceptedByEmail) && (
                  <p className="text-xs text-content-secondary">
                    {config.accepted_by_email || config.acceptedByEmail}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-content-secondary uppercase tracking-wide">Accepted At</p>
                <p className="text-sm font-medium text-content-primary">
                  {config.accepted_at || config.acceptedAt || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-content-secondary uppercase tracking-wide">Channel</p>
                <p className="text-sm font-medium text-content-primary">
                  {config.channel || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-content-secondary uppercase tracking-wide">Locked Total</p>
                <p className="text-sm font-bold text-content-primary">
                  {formatCurrency(config.total_cents ?? config.totalCents ?? 0)}
                </p>
              </div>
            </div>

            {/* Signature image — IDI-116's core defect. */}
            <div>
              <p className="text-xs text-content-secondary uppercase tracking-wide mb-2">Signature</p>
              {signatureUrl ? (
                <img
                  src={signatureUrl}
                  alt="Customer signature"
                  className="border border-border-default rounded bg-white max-h-48"
                  data-testid="signature-image"
                />
              ) : (
                <p className="text-sm text-content-secondary" data-testid="signature-unavailable">
                  {signatureError || 'Loading signature…'}
                </p>
              )}
            </div>

            {/* Line-item snapshot — the frozen configuration itself. */}
            {Array.isArray(config.line_items || config.lineItems) && (config.line_items || config.lineItems).length > 0 && (
              <div>
                <p className="text-xs text-content-secondary uppercase tracking-wide mb-2">Accepted Line Items</p>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Item</TableHeader>
                      <TableHeader align="right">Total</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(config.line_items || config.lineItems).map((li, idx) => (
                      <TableRow key={li.id || idx}>
                        <TableCell>
                          {li.item_name || li.itemName || li.name || li.description || '—'}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(li.line_total ?? li.lineTotal ?? 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {cleanTerms && (
              <div>
                <p className="text-xs text-content-secondary uppercase tracking-wide mb-1">Terms &amp; Conditions</p>
                <div
                  className={`text-xs text-content-secondary${termsArePlain ? ' whitespace-pre-wrap' : ''}`}
                  data-testid="accepted-config-terms"
                  dangerouslySetInnerHTML={{ __html: cleanTerms }}
                />
              </div>
            )}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </DialogFooter>
    </Dialog>
  );
}
